import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { debounce } from "lodash";
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { v4 as uuid4 } from "uuid";

import { useGoogleServices } from "@ryde/redux";
import {
  API_BASE_LANDINGS,
  appLanguageSelector,
  ICoordinate,
  IPlacePrediction,
  IValidationsYupRoot,
  Log,
  SupportedLanguage,
  ValidationHelpers,
} from "@ryde/shared";

import { LandingAutocompleteOption } from "./landing-autocomplete-option";
import {
  landingConvertStringCoordinate,
  landingIsStringCoordinate,
} from "./landing-map.helpers";

interface IAddressProps {
  value: IPlacePrediction | null;
  defaultOptions?: IPlacePrediction[];
  onChange: (
    value: IPlacePrediction | null,
    sessionToken?: string | null,
  ) => void;
  onOptionsChange?: (options: IPlacePrediction[]) => void;
  label?: string | null;
  placeholder?: string | null;
  error?: boolean;
  formValidation?: IValidationsYupRoot | null;
  inputId?: string;
}

const debounceTimeout = 400;

export const LandingAddressAutocomplete: FC<
  IAddressProps &
    Partial<
      Omit<AutocompleteProps<IPlacePrediction, false, false, false>, "onChange">
    >
> = ({
  value,
  defaultOptions = [],
  onChange,
  onOptionsChange,
  label,
  placeholder,
  error,
  formValidation,
  inputId,
  ...props
}) => {
  const { t } = useTranslation();
  const language = useRecoilValue(appLanguageSelector);
  const { searchAddressByGeo, searchPlacesByInput } = useGoogleServices(
    API_BASE_LANDINGS,
    true,
  );
  const [inputValue, setInputValue] = useState(value?.text?.text || "");
  const [options, setOptions] = useState<IPlacePrediction[]>(defaultOptions);
  const sessionTokenRef = useRef<string | null>(null);
  const isActiveRef = useRef(true);

  const handleOptionsChange = useCallback(
    (newOptions: IPlacePrediction[]) => {
      setOptions(newOptions);
      onOptionsChange?.(newOptions);
    },
    [onOptionsChange],
  );

  const callbackResolver = useCallback(
    async (results: IPlacePrediction[] | null): Promise<void> => {
      if (!isActiveRef.current) {
        return;
      }

      let newOptions: IPlacePrediction[] = [];

      if (value) {
        newOptions = [value];
      }

      if (results) {
        newOptions = [...newOptions, ...results];
      }

      handleOptionsChange(newOptions);
    },
    [handleOptionsChange, value],
  );

  const fetch = useMemo(
    () =>
      debounce(
        async (request: {
          input: string;
          language: SupportedLanguage | undefined;
        }): Promise<void> => {
          if (!sessionTokenRef.current) {
            sessionTokenRef.current = uuid4();
          }
          const result = await searchPlacesByInput({
            inputParams: { ...request, sessionToken: sessionTokenRef.current },
          });
          if (result?.success) {
            callbackResolver(result?.data ?? []);
          } else {
            Log.error("Fetch error", result?.error);
          }
        },
        debounceTimeout,
      ),
    [searchPlacesByInput, callbackResolver],
  );

  const fetchByLatLngCoordinate = useMemo(
    () =>
      debounce(
        async (geoParams: {
          location: ICoordinate;
          language: SupportedLanguage | undefined;
        }): Promise<void> => {
          const result = await searchAddressByGeo({ geoParams });
          if (result?.success) {
            const predictions = result?.data ? [result?.data] : [];
            callbackResolver(predictions);
          } else {
            Log.error("Fetch error", result?.error);
          }
        },
        debounceTimeout,
      ),
    [callbackResolver, searchAddressByGeo],
  );

  const handleChange = (
    event: unknown,
    newValue: IPlacePrediction | null,
  ): void => {
    handleOptionsChange(newValue ? [newValue, ...options] : []);
    const token = sessionTokenRef.current;
    sessionTokenRef.current = null;
    onChange(newValue, token);
  };

  const onInputChange = async (event: SyntheticEvent, newValue: string) => {
    if (!newValue) {
      sessionTokenRef.current = null;
    }
    setInputValue(newValue);
  };

  useEffect(() => {
    isActiveRef.current = true;

    const isCoordinate = landingIsStringCoordinate(inputValue);

    if (isCoordinate) {
      fetchByLatLngCoordinate({
        location: landingConvertStringCoordinate(inputValue) as ICoordinate,
        language,
      });
    } else {
      fetch({
        input: inputValue,
        language,
      });
    }

    return () => {
      isActiveRef.current = false;
      fetch.cancel();
      fetchByLatLngCoordinate.cancel();
    };
  }, [inputValue, language, fetch, fetchByLatLngCoordinate]);

  return (
    <Autocomplete
      id={inputId}
      getOptionLabel={option =>
        typeof option === "string" ? option : (option?.text?.text ?? "")
      }
      clearOnBlur={false}
      value={value}
      inputValue={inputValue}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      noOptionsText={t("rideMap.NO_LOCATIONS")}
      filterOptions={e => e}
      {...props}
      onInputChange={onInputChange}
      renderInput={params => (
        <TextField
          {...params}
          data-e2e="address-map-field"
          label={label ?? ""}
          placeholder={placeholder || t("rideMap.ADD_LOCATION")}
          fullWidth
          error={error}
          required={ValidationHelpers.hasRequiredRule(formValidation)}
        />
      )}
      renderOption={(renderProps, option) => (
        <LandingAutocompleteOption renderProps={renderProps} option={option} />
      )}
      onChange={handleChange}
    />
  );
};
